import { IN, NOT_HAS_PROPERTY, NOT_IN, EQ, LT, GT } from 'framework-listing-lib/utils/filters';
import isEqual from 'hs-lodash/isEqual';
import { CONTENT_PLATFORM_PROPERTY_NAME } from 'content-management-data-lib';
import { isEqualOperator, isInOperator, isInOrNotInOperator } from '../utils/typeCheckUtils';
import { PROPERTIES_WITH_SELF_USER_IDENTIFIER, SELF_USER_IDENTIFIER } from '../constants/filters';
import { ApprovalStatusToFilterValue, FilterablePublishStates } from '../constants/filters';
const getHasFiltersApplied = (requestOptions, baseFilters) => {
  return !(requestOptions.filterGroups.length === 1 && isEqual(new Set(requestOptions.filterGroups[0].filters.filter(f => f.property !== CONTENT_PLATFORM_PROPERTY_NAME.BUSINESS_UNIT &&
  // framework-listing-lib automatically handles async action reconciliation
  // for certain actions by filtering out an object, but we don't want to consider that
  // as a filter being applied by the user. Also, it is not a CMP property, so it is hardcoded here
  f.property !== 'hs_object_id')), new Set(baseFilters)));
};
const getIsSearching = requestOptions => requestOptions.query !== '';
const getIsFilteringOrSearching = (requestOptions, defaultFilters) => getHasFiltersApplied(requestOptions, defaultFilters) || getIsSearching(requestOptions);
const correctUserIdInFilterValue = (filter, userId) => {
  if (isInOrNotInOperator(filter) && filter.values.includes(SELF_USER_IDENTIFIER)) {
    const values = filter.values.map(value => value === SELF_USER_IDENTIFIER ? `${userId}` : value);
    if (isInOperator(filter)) {
      return IN(filter.property, values);
    } else {
      return NOT_IN(filter.property, values);
    }
  }
  return filter;
};
export const addRequiredFilters = (filters, hasArchivedFilter, isFilteringOrSearching) => {
  /*
   * The following filter corrections CANNOT be overridden by clients because they
   * are essential to consistent behavior with searching/filtering in the table
   */

  // Always omit "archived in dashboard" objects unless specifically filtering for them
  if (!hasArchivedFilter) {
    filters.push(EQ(CONTENT_PLATFORM_PROPERTY_NAME.ARCHIVED, false));
  }

  // Omit child (translation) rows from request if not applying any filter/search query
  if (!isFilteringOrSearching) {
    filters.push(NOT_HAS_PROPERTY(CONTENT_PLATFORM_PROPERTY_NAME.TRANSLATED_FROM_ID));
  }
  return filters;
};
export const getMergedContentGroupAssociationsFilterValues = (filter, mergedContentGroupAssociations) => {
  return IN(filter.property, filter.values.reduce((groupIdsAcc, groupId) => {
    groupIdsAcc.push(groupId);
    if (mergedContentGroupAssociations[groupId]) {
      groupIdsAcc.push(...mergedContentGroupAssociations[groupId]);
    }
    return groupIdsAcc;
  }, []));
};
const correctPublishStateFilter = (filter, isUngatedToUseResolvedState, acc) => {
  const filterValues = filter.values;
  const hasScheduledFilter = filterValues.includes(FilterablePublishStates.SCHEDULED);
  const hasPublishedFilter = filterValues.includes(FilterablePublishStates.PUBLISHED);
  const hasDraftFilter = filterValues.includes(FilterablePublishStates.DRAFT);
  if (!isUngatedToUseResolvedState && hasDraftFilter && hasScheduledFilter && hasPublishedFilter) {
    // same as not applying the publish status filter so don't add to the request filters
    return;
  }
  const updatedFilter = isUngatedToUseResolvedState ? Object.assign({}, filter, {
    property: CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE,
    values: hasPublishedFilter ? [...filterValues, 'SCHEDULED_UNPUBLISH', 'SCHEDULED_UPDATE'] : filterValues
  }) : Object.assign({}, filter, {
    values: filterValues.filter(v => v !== FilterablePublishStates.SCHEDULED)
  });
  if (!isUngatedToUseResolvedState) {
    if (hasScheduledFilter && !hasPublishedFilter) {
      acc.push(GT(CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE, Date.now()));
      if (!hasDraftFilter) {
        updatedFilter.values.push(FilterablePublishStates.DRAFT);
      }
    } else if (hasPublishedFilter && !hasScheduledFilter) {
      acc.push(LT(CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE, Date.now()));
    }
  }
  acc.push(updatedFilter);
};
const correctResolvedPublishedStateFilter = filter => {
  const hasPublishedFilter = filter.values.includes(FilterablePublishStates.PUBLISHED);
  return Object.assign({}, filter, {
    values: hasPublishedFilter ? [...filter.values, 'SCHEDULED_UNPUBLISH', 'SCHEDULED_UPDATE'] : filter.values
  });
};
const correctFilterPropertyValues = (filters, customCorrectedAndAdditionalFiltersMap, userId, mergedContentGroupAssociations, isFilteringOrSearching, isUngatedToUseResolvedState) => {
  const currentFilterPropertyNames = filters.reduce((acc, filter) => {
    acc.add(filter.property);
    return acc;
  }, new Set());
  let hasArchivedFilter = false;

  // Correct filters for Content Platform properties
  const correctedFilters = filters.reduce((acc, filter) => {
    // Correct filter values
    if (PROPERTIES_WITH_SELF_USER_IDENTIFIER.includes(filter.property)) {
      acc.push(correctUserIdInFilterValue(filter, userId));
    } else if ((filter.property === CONTENT_PLATFORM_PROPERTY_NAME.STATE || filter.property === CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE) && isInOrNotInOperator(filter)) {
      hasArchivedFilter = filter.values.includes(FilterablePublishStates.ARCHIVED);
      if (hasArchivedFilter) {
        acc.push(EQ(CONTENT_PLATFORM_PROPERTY_NAME.ARCHIVED, true));
      } else if (filter.property === CONTENT_PLATFORM_PROPERTY_NAME.STATE) {
        correctPublishStateFilter(filter, isUngatedToUseResolvedState, acc);
      } else {
        acc.push(correctResolvedPublishedStateFilter(filter));
      }
    } else if (filter.property === CONTENT_PLATFORM_PROPERTY_NAME.APPROVAL_PUBLISH_STATUS && isEqualOperator(filter)) {
      acc.push(Object.assign({}, filter, {
        value: ApprovalStatusToFilterValue[filter.value]
      }));
    } else if (filter.property === CONTENT_PLATFORM_PROPERTY_NAME.GROUP_ID && isInOrNotInOperator(filter)) {
      acc.push(getMergedContentGroupAssociationsFilterValues(filter, mergedContentGroupAssociations));
    } else if (filter.property === CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE) {
      acc.push(filter, NOT_IN(isUngatedToUseResolvedState ? CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE : CONTENT_PLATFORM_PROPERTY_NAME.STATE, [FilterablePublishStates.DRAFT]));
    } else if (filter.property === 'hs_object_id') {
      acc.push(Object.assign({}, filter, {
        property: CONTENT_PLATFORM_PROPERTY_NAME.ID
      }));
    } else {
      acc.push(filter);
    }
    return acc;
  }, []);

  // Override filters and add additional filters based on client-provided mapping
  if (customCorrectedAndAdditionalFiltersMap) {
    Object.keys(customCorrectedAndAdditionalFiltersMap).forEach(propertyName => {
      const clientDefinedFilter = customCorrectedAndAdditionalFiltersMap[propertyName];
      if (currentFilterPropertyNames.has(propertyName)) {
        const foundIndex = correctedFilters.findIndex(filterOp => filterOp.property === propertyName);
        correctedFilters[foundIndex] = clientDefinedFilter;
      } else {
        correctedFilters.push(clientDefinedFilter);
      }
    });
  }
  return addRequiredFilters(correctedFilters, hasArchivedFilter, isFilteringOrSearching);
};
function correctSorts(sorts) {
  return sorts.map(sort => {
    if (sort.property === CONTENT_PLATFORM_PROPERTY_NAME.STATE) {
      return Object.assign({}, sort, {
        property: CONTENT_PLATFORM_PROPERTY_NAME.RESOLVED_STATE
      });
    }
    return sort;
  });
}
function getHiddenFilterFilterOperators(hiddenFilter) {
  return hiddenFilter ? hiddenFilter.filterGroups[0].filters : [];
}
export const correctRequestOptions = ({
  requestOptions,
  userInfo,
  filterValidationConfig,
  mergedContentGroupAssociations = {},
  hiddenFilter,
  isUngatedToUseResolvedState
}) => {
  const {
    getCorrectedAndAdditionalFiltersMap,
    isFilterAvailableForUser
  } = filterValidationConfig;
  const correctedAndAdditionalFiltersMap = getCorrectedAndAdditionalFiltersMap(requestOptions);
  const requestOptionsWithFinalFilters = Object.assign({}, requestOptions, {
    sorts: correctSorts(requestOptions.sorts),
    filterGroups: [{
      filters: [...requestOptions.filterGroups[0].filters.filter(filter => isFilterAvailableForUser(filter, userInfo))]
    }]
  });

  // Do not consider hidden filter operators as "filtering" state
  // since those are controlled from outside the lib
  const baseFilterOperatorsToCompare = getHiddenFilterFilterOperators(hiddenFilter);
  const isFilteringOrSearching = getIsFilteringOrSearching(requestOptionsWithFinalFilters, baseFilterOperatorsToCompare);
  requestOptionsWithFinalFilters.filterGroups[0].filters = correctFilterPropertyValues(requestOptionsWithFinalFilters.filterGroups[0].filters, correctedAndAdditionalFiltersMap, userInfo.user.user_id, mergedContentGroupAssociations, isFilteringOrSearching, isUngatedToUseResolvedState);
  return requestOptionsWithFinalFilters;
};